import firebase from 'firebase/app';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLoading } from '../../hooks/useLoading';
import { POST_PAGE_TYPES } from '../../constants/postPageTypes';
import { useAuth } from '../../hooks/useAuth';
import { INVITE_ALL_USERS } from '../../constants/post';

const COLLECTION_NAME = 'posts';
const DEFAULT_BATCH_SIZE = 12;

const getCollection = () => firebase.firestore().collection(COLLECTION_NAME);

export const usePosts = (currentPostPage, needRefresh, category) => {
    const {
        currentUser: { uid: userId, email },
    } = useAuth();
    const {
        loadingState: { isLoading },
        loadPromise,
    } = useLoading();

    const [totalPostsCount, setTotalPostsCount] = useState(0);
    const [postsCount, setPostsCount] = useState(0);
    const [posts, setPosts] = useState(new Map());

    const initialQuery = useMemo(() => {
        setPosts(new Map());

        let query = getCollection();

        if (category) {
            query = query.where('category', '==', category);
        }

        switch (currentPostPage) {
            case POST_PAGE_TYPES.PENDING_POSTS: {
                return query
                    .where('isApprovedByAdmin', '==', false)
                    .where('isDeleted', '==', false);
            }
            case POST_PAGE_TYPES.INVITATIONS: {
                return query
                    .where('isDeleted', '==', false)
                    .where(`inviteColleagues`, 'array-contains-any', [email, INVITE_ALL_USERS]);
            }
            case POST_PAGE_TYPES.MY_POSTS: {
                return query.where('publisherUid', '==', userId).where('isDeleted', '==', false);
            }
        }
        // needRefresh should be here for data reload
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPostPage, userId, email, needRefresh, category]);

    useEffect(() => {
        loadPromise(
            initialQuery.get().then(snap => {
                const collectionSize = snap.size;
                setTotalPostsCount(collectionSize);
                return setPostsCount(
                    DEFAULT_BATCH_SIZE <= collectionSize ? DEFAULT_BATCH_SIZE : collectionSize
                );
            })
        );
    }, [loadPromise, initialQuery]);

    const loadMore = useCallback(
        async ({ startIndex, stopIndex }) => {
            const newPosts = new Map(posts);
            const lastPost = posts.size ? Array.from(posts.values())[posts.size - 1] : null;

            const limit = stopIndex - startIndex + 1;
            let query = initialQuery.orderBy('createdTimestamp', 'desc');

            if (lastPost) {
                query = query.startAfter(lastPost.createdTimestamp);
            }

            return query
                .limit(limit)
                .get()
                .then(snap => {
                    snap.docs.map((doc, index) => {
                        newPosts.set(startIndex + index, doc.data());
                    });

                    setPosts(newPosts);
                    setPostsCount(
                        newPosts.size + 4 <= totalPostsCount ? newPosts.size + 4 : totalPostsCount
                    );

                    return Promise.resolve();
                });
        },
        [posts, totalPostsCount, initialQuery]
    );

    return {
        dataCount: postsCount,
        data: posts,
        isLoading,
        loadMore,
    };
};
